import { ethers } from 'ethers';
import BridgeABI from '../contracts/BridgeABI.json';
import { getBaseProvider, getEtherlinkProvider } from '../utils/providers';

// Pre-encoded: Options.newOptions().addExecutorLzReceiveOption(200000, 0).toHex()
// type=3 (EXECUTOR), option_type=1 (LZ_RECEIVE), gas=200000, value=0
const BRIDGE_EXTRA_OPTIONS = '0x00030100110100000000000000000000000000030d40';

const appChainId = parseInt(process.env.REACT_APP_CHAIN_ID || '8453', 10);
const isTestnet = appChainId !== 8453;

const etherlinkChainId = parseInt(
  process.env.REACT_APP_ETHERLINK_CHAIN_ID || (isTestnet ? '128123' : '42793'),
  10
);

export const CHAIN_CONFIG = {
  base: {
    chainId: appChainId,
    chainIdHex: '0x' + appChainId.toString(16),
    name: process.env.REACT_APP_BASE_CHAIN_NAME || (isTestnet ? 'Base Sepolia' : 'Base'),
    lzEid: parseInt(process.env.REACT_APP_BASE_LZ_EID || (isTestnet ? '40245' : '30184'), 10),
    nftContract: process.env.REACT_APP_DEPLOYED_NFT_CONTRACT || '',
    explorer: process.env.REACT_APP_BASE_EXPLORER_URL || (isTestnet ? 'https://sepolia.basescan.org' : 'https://basescan.org'),
    nativeCurrency: {
      name: process.env.REACT_APP_BASE_NATIVE_NAME || 'Ethereum',
      symbol: process.env.REACT_APP_BASE_NATIVE_SYMBOL || 'ETH',
      decimals: 18,
    },
    rpcUrl: process.env.REACT_APP_RPC_URL || '',
  },
  etherlink: {
    chainId: etherlinkChainId,
    chainIdHex: '0x' + etherlinkChainId.toString(16),
    name: process.env.REACT_APP_ETHERLINK_CHAIN_NAME || (isTestnet ? 'Etherlink Testnet' : 'Etherlink'),
    lzEid: parseInt(process.env.REACT_APP_ETHERLINK_LZ_EID || (isTestnet ? '40161' : '30292'), 10),
    nftContract: process.env.REACT_APP_ETHERLINK_NFT_CONTRACT || '',
    explorer: process.env.REACT_APP_ETHERLINK_EXPLORER_URL
      || (isTestnet ? 'https://testnet.explorer.etherlink.com' : 'https://explorer.etherlink.com'),
    nativeCurrency: {
      name: process.env.REACT_APP_ETHERLINK_NATIVE_NAME || 'Tezos',
      symbol: process.env.REACT_APP_ETHERLINK_NATIVE_SYMBOL || 'XTZ',
      decimals: 18,
    },
    rpcUrl: process.env.REACT_APP_ETHERLINK_RPC_URL || '',
  },
};

/**
 * Get the source and destination chain config based on which chain the NFT is on.
 */
export function getChainPair(nftChain) {
  if (nftChain === 'Etherlink') {
    return { source: CHAIN_CONFIG.etherlink, destination: CHAIN_CONFIG.base };
  }
  return { source: CHAIN_CONFIG.base, destination: CHAIN_CONFIG.etherlink };
}

/**
 * Get a read-only provider for fee quoting (doesn't need wallet connection).
 */
export function getReadProvider(chain) {
  if (chain === 'Etherlink') {
    return getEtherlinkProvider();
  }
  return getBaseProvider();
}

/**
 * Quote the LayerZero fee for bridging a single NFT.
 * Returns { nativeFee, lzTokenFee, gasCost } — gasCost is BigNumber (0 if estimation fails).
 */
export async function quoteBridgeFee(nftChain, tokenId, recipientAddress) {
  const { source, destination } = getChainPair(nftChain);
  const provider = getReadProvider(nftChain);
  if (!provider) throw new Error(`No RPC provider for ${nftChain}`);

  const contract = new ethers.Contract(source.nftContract, BridgeABI.abi, provider);
  const recipientBytes32 = ethers.utils.hexZeroPad(recipientAddress, 32);

  const fee = await contract.quoteBridgeTo(
    tokenId,
    destination.lzEid,
    recipientBytes32,
    BRIDGE_EXTRA_OPTIONS,
    false
  );

  // Try to estimate gas so the UI can show a more accurate total
  let gasCost = ethers.BigNumber.from(0);
  try {
    const overpay = fee.nativeFee.mul(110).div(100);
    const gasEstimate = await contract.estimateGas.bridgeTo(
      tokenId,
      destination.lzEid,
      recipientBytes32,
      BRIDGE_EXTRA_OPTIONS,
      recipientAddress,
      { value: overpay, from: recipientAddress }
    );
    const feeData = await provider.getFeeData();
    const gasPrice = feeData.maxFeePerGas || feeData.gasPrice || ethers.BigNumber.from(0);
    gasCost = gasEstimate.mul(gasPrice);
  } catch (_) {
    // Gas estimation may fail (e.g., insufficient balance for simulation) — just show LZ fee
  }

  return { nativeFee: fee.nativeFee, lzTokenFee: fee.lzTokenFee, gasCost };
}

/**
 * Execute the bridge transaction for a single NFT.
 * Returns the transaction object (caller should await tx.wait()).
 */
export async function executeBridge(signer, nftChain, tokenId, recipientAddress) {
  const { source, destination } = getChainPair(nftChain);
  const contract = new ethers.Contract(source.nftContract, BridgeABI.abi, signer);
  const recipientBytes32 = ethers.utils.hexZeroPad(recipientAddress, 32);
  const signerAddress = await signer.getAddress();

  // Quote fee fresh (may differ from earlier estimate)
  const fee = await contract.quoteBridgeTo(
    tokenId,
    destination.lzEid,
    recipientBytes32,
    BRIDGE_EXTRA_OPTIONS,
    false
  );

  // 10% buffer — contract refunds excess automatically
  const overpay = fee.nativeFee.mul(110).div(100);
  const symbol = source.nativeCurrency.symbol;

  // Estimate gas to catch insufficient balance early with a clear message
  let gasLimit;
  try {
    gasLimit = await contract.estimateGas.bridgeTo(
      tokenId,
      destination.lzEid,
      recipientBytes32,
      BRIDGE_EXTRA_OPTIONS,
      signerAddress,
      { value: overpay }
    );
  } catch (err) {
    // Parse RPC "insufficient funds for gas * price + value" error
    const parsed = parseInsufficientFundsRpcError(err, symbol, source.name);
    if (parsed) throw parsed;
    throw err;
  }

  // Verify balance covers gas + value
  const balance = await signer.getBalance();
  const feeData = await signer.provider.getFeeData();
  const gasPrice = feeData.maxFeePerGas || feeData.gasPrice || ethers.BigNumber.from(0);
  const totalNeeded = overpay.add(gasLimit.mul(gasPrice));

  if (balance.lt(totalNeeded)) {
    const need = parseFloat(ethers.utils.formatEther(totalNeeded)).toFixed(6);
    const have = parseFloat(ethers.utils.formatEther(balance)).toFixed(6);
    throw new Error(
      `Insufficient ${symbol} balance on ${source.name}. Need ~${need} ${symbol} (fee + gas) but have ${have} ${symbol}`
    );
  }

  const tx = await contract.bridgeTo(
    tokenId,
    destination.lzEid,
    recipientBytes32,
    BRIDGE_EXTRA_OPTIONS,
    signerAddress,
    { value: overpay, gasLimit }
  );

  return { tx, fee };
}

/**
 * Parse RPC "insufficient funds for gas * price + value" errors into user-friendly messages.
 */
function parseInsufficientFundsRpcError(err, symbol, chainName) {
  const bodyStr = typeof err.body === 'string' ? err.body
    : typeof err.error?.body === 'string' ? err.error.body : '';
  const combined = bodyStr + ' ' + (err.message || '');

  const wantMatch = combined.match(/want\s+(\d+)/);
  const haveMatch = combined.match(/have\s+(\d+)/);
  if (wantMatch && haveMatch) {
    const need = parseFloat(ethers.utils.formatEther(ethers.BigNumber.from(wantMatch[1]))).toFixed(6);
    const have = parseFloat(ethers.utils.formatEther(ethers.BigNumber.from(haveMatch[1]))).toFixed(6);
    return new Error(
      `Insufficient ${symbol} balance on ${chainName}. Need ~${need} ${symbol} (fee + gas) but have ${have} ${symbol}`
    );
  }

  if (combined.includes('insufficient funds') || combined.includes('INSUFFICIENT_FUNDS')) {
    return new Error(`Insufficient ${symbol} balance on ${chainName} for fee + gas`);
  }
  return null;
}

/**
 * Switch the connected wallet to the target chain.
 * For external wallets (MetaMask etc.) uses wallet_switchEthereumChain.
 * Returns true on success.
 */
export async function switchToChain(walletProvider, chainKey) {
  const config = CHAIN_CONFIG[chainKey];
  if (!config) throw new Error(`Unknown chain: ${chainKey}`);

  try {
    await walletProvider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: config.chainIdHex }],
    });
    return true;
  } catch (error) {
    // 4902 = chain not added to wallet
    if (error.code === 4902) {
      await walletProvider.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: config.chainIdHex,
            chainName: config.name,
            rpcUrls: [config.rpcUrl],
            blockExplorerUrls: [config.explorer],
            nativeCurrency: config.nativeCurrency,
          },
        ],
      });
      return true;
    }
    throw error;
  }
}

/**
 * Detect if a signer is a Sogni session wallet (ethers.Wallet with JsonRpcProvider)
 * vs an external wallet (Web3Provider signer).
 */
export function isSogniSessionWallet(signer) {
  return signer && typeof signer.privateKey === 'string';
}

/**
 * For Sogni session wallets, create a signer connected to a different chain's RPC.
 */
export function createSessionSignerForChain(signer, chainKey) {
  if (!isSogniSessionWallet(signer)) return null;
  const config = CHAIN_CONFIG[chainKey];
  const provider = new ethers.providers.JsonRpcProvider(config.rpcUrl);
  return new ethers.Wallet(signer.privateKey, provider);
}

/**
 * Get the LayerZero Scan API base URL based on network.
 */
function getLzScanApiUrl() {
  return appChainId === 8453
    ? 'https://scan.layerzero-api.com/v1'
    : 'https://scan-testnet.layerzero-api.com/v1';
}

/**
 * Poll LayerZero Scan API for the delivery status of a bridge transaction.
 * Returns { status, message } or null if not yet indexed.
 */
export async function pollLzScanStatus(txHash) {
  try {
    const baseUrl = getLzScanApiUrl();
    const response = await fetch(`${baseUrl}/messages/tx/${txHash}`);
    if (!response.ok) return null;

    const data = await response.json();
    if (!data.data || data.data.length === 0) return null;

    const message = data.data[0];
    return {
      status: message.status?.name || 'INFLIGHT',
      message: message.status?.message || '',
    };
  } catch (err) {
    console.error('LZ Scan poll error:', err);
    return null;
  }
}

/**
 * Get the LayerZero Scan URL for a transaction.
 */
export function getLzScanUrl(txHash) {
  return `https://layerzeroscan.com/tx/${txHash}`;
}

/**
 * Get the block explorer URL for a transaction.
 */
export function getExplorerTxUrl(chainKey, txHash) {
  const config = CHAIN_CONFIG[chainKey];
  return `${config.explorer}/tx/${txHash}`;
}
